import type { VehicleSummary } from '@/types'

import { useMemo } from 'react'
import { formatPrice } from '@/lib/format'
import Eyebrow from '@/components/generals/eyebrow'
import VehicleCatalog from '@/components/vehicles/vehicle-catalog'

interface FleetSectionProps {
    vehicles: Array<VehicleSummary>
    onReserve: (vehicle: VehicleSummary) => void
}

export default function FleetSection({ vehicles, onReserve }: FleetSectionProps) {
    const stats = useMemo(() => {
        const prices = vehicles.map(vehicle => vehicle.precio_hora).filter((price): price is number => price !== null)

        return {
            total: vehicles.length,
            maxPassengers: vehicles.reduce((max, vehicle) => Math.max(max, vehicle.pasajeros), 0),
            minPrice: prices.length > 0 ? Math.min(...prices) : null
        }
    }, [vehicles])

    return (
        <section id='vehiculos' aria-labelledby='vehiculos-titulo' className='relative scroll-mt-20 overflow-hidden pt-20 pb-24 sm:pt-24 lg:pt-28 lg:pb-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-[34rem] bg-[radial-gradient(60%_70%_at_50%_0%,color-mix(in_oklch,var(--champagne)_28%,transparent)_0%,transparent_70%)]' />

            <div className='relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <header className='mx-auto max-w-3xl text-center'>
                    <Eyebrow align='center'>
                        Nuestra flota
                    </Eyebrow>

                    <h1 id='vehiculos-titulo' className='mt-6 font-heading text-5xl leading-[1.05] font-normal tracking-tight text-balance text-foreground sm:text-6xl lg:text-7xl'>
                        Nuestros <em className='text-gold italic'>vehículos</em>
                    </h1>

                    <p className='mx-auto mt-6 max-w-2xl text-base leading-relaxed text-pretty text-muted-foreground sm:text-lg'>
                        Vehículos de alta gama con chófer para traslados, bodas, eventos y viajes de empresa. Filtra por tipo de vehículo, pasajeros u ocasión y solicita tu reserva en un par de clics.
                    </p>

                    {stats.total > 0 && (
                        <dl className='mx-auto mt-10 grid max-w-xl grid-cols-3 divide-x divide-border/80 rounded-2xl border border-border/80 bg-card/70 py-5 backdrop-blur'>
                            <div className='px-3'>
                                <dt className='text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                    En flota
                                </dt>

                                <dd className='mt-1.5 font-heading text-2xl text-foreground sm:text-3xl'>
                                    {stats.total}
                                </dd>
                            </div>

                            <div className='px-3'>
                                <dt className='text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                    Hasta
                                </dt>

                                <dd className='mt-1.5 font-heading text-2xl text-foreground sm:text-3xl'>
                                    <span>{stats.maxPassengers}</span>
                                    <span className='ml-1 font-sans text-sm text-muted-foreground'>plazas</span>
                                </dd>
                            </div>

                            <div className='px-3'>
                                <dt className='text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                    Desde
                                </dt>

                                <dd className='mt-1.5 font-heading text-2xl text-foreground sm:text-3xl'>
                                    {stats.minPrice !== null
                                        ? (
                                            <>
                                                <span>{formatPrice(stats.minPrice)}</span>
                                                <span className='ml-0.5 font-sans text-sm text-muted-foreground'>/h</span>
                                            </>
                                        )
                                        : <span className='text-xl'>Consultar</span>}
                                </dd>
                            </div>
                        </dl>
                    )}
                </header>

                <VehicleCatalog vehicles={vehicles} onReserve={onReserve} />
            </div>
        </section>
    )
}
